import React, { useEffect, useMemo, useState } from "react";
import { Avatar } from "@fluentui/react-components";
import { MoreVertical, Pencil, Trash2, Mail, Phone } from "lucide-react";
import HoverCard from "../hoverCard/HoverCard";
import useNavigation from "../../utils/useNavigation";
import {
  entityTypeToParentType,
  parentTypeToEntityType,
  getLabelByType,
  formatPhoneForTelHref,
  formatPhoneNumberForDisplay,
} from "../../helpers/utils";
import styles from "./ContactsPanel.module.scss";

const displayName = (contact) => contact.name || contact.email || contact.phone || "";

// Groups the flat "View" result by owning entity, and lays it out as a tree with the node
// being viewed in the middle (highlighted): ancestor branches (contacts shared down from a
// Client/Portfolio/Program above this node) render above it, descendant branches (a rolled-up
// Portfolio/Program/Project's own contacts) render below - so the hierarchy direction is
// always visible, not just a flat list of "other" contacts.
const ContactOrgChart = ({ contacts, parentType, parentId, parentName, onEdit, onDelete }) => {
  const { navigateTo } = useNavigation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const currentRank = parentTypeToEntityType(parentType);

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-chip-menu]")) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  const { ownContacts, ancestorLevels, descendantLevels } = useMemo(() => {
    const own = [];
    const branchesByOwner = new Map();

    contacts.forEach((contact) => {
      if (contact.isDirect) {
        own.push(contact);
        return;
      }
      const ownerKey = contact.owner?.id || "unknown";
      if (!branchesByOwner.has(ownerKey)) {
        branchesByOwner.set(ownerKey, { owner: contact.owner, contacts: [] });
      }
      branchesByOwner.get(ownerKey).contacts.push(contact);
    });

    const byName = (a, b) => displayName(a).localeCompare(displayName(b));
    own.sort(byName);

    const allBranches = Array.from(branchesByOwner.values());
    allBranches.forEach((branch) => branch.contacts.sort(byName));

    // Groups branches by their owner's hierarchy rank (Client=0 < Portfolio=1 < Program=2 <
    // Project=3), sorted ascending, so each level of the hierarchy renders as its own row
    // instead of flattening e.g. a Client and a Portfolio ancestor onto the same line.
    const groupByRank = (branches) => {
      const byRank = new Map();
      branches.forEach((branch) => {
        const rank = branch.owner?.type;
        if (!byRank.has(rank)) byRank.set(rank, []);
        byRank.get(rank).push(branch);
      });
      return Array.from(byRank.entries())
        .sort(([a], [b]) => a - b)
        .map(([rank, branches]) => ({ rank, branches }));
    };

    return {
      ownContacts: own,
      ancestorLevels: groupByRank(allBranches.filter((b) => b.owner?.type < currentRank)),
      descendantLevels: groupByRank(allBranches.filter((b) => b.owner?.type > currentRank)),
    };
  }, [contacts, currentRank]);

  const goTo = (type, id, name) => navigateTo(entityTypeToParentType(type), id, name);

  const renderChip = (contact, { editable }) => (
    <div key={contact.id} className={styles.chartChip}>
      <HoverCard
        HoverCardTrigger={<Avatar color="colorful" size={24} name={contact.name || contact.email} />}
        AvatarId={contact.id}
        AvatarName={contact.name}
        AvatarEmail={contact.email}
        AvatarPhone={contact.phone}
        AvatarIsExternal={true}
        CanSendMessage={false}
      />
      <span className={styles.chartChipName}>{contact.name || contact.email || contact.phone}</span>
      {contact.email && (
        <a
          href={`mailto:${contact.email}`}
          className={styles.chipIconBtn}
          title={contact.email}
          onClick={(e) => e.stopPropagation()}
        >
          <Mail size={12} />
        </a>
      )}
      {contact.phone && (
        <a
          href={formatPhoneForTelHref(contact.phone)}
          className={styles.chipIconBtn}
          title={formatPhoneNumberForDisplay(contact.phone)}
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={12} />
        </a>
      )}
      {editable && (
        <div className={styles.chipMenuWrap} data-chip-menu>
          <button
            type="button"
            className={styles.chipIconBtn}
            title="More actions"
            onClick={() => setOpenMenuId((prev) => (prev === contact.id ? null : contact.id))}
          >
            <MoreVertical size={14} />
          </button>
          {openMenuId === contact.id && (
            <div className={styles.chipActionMenu} data-chip-menu>
              <button
                type="button"
                className={styles.chipActionMenuItem}
                onClick={() => {
                  setOpenMenuId(null);
                  onEdit(contact);
                }}
              >
                <Pencil size={13} />
                <span>Edit</span>
              </button>
              <button
                type="button"
                className={`${styles.chipActionMenuItem} ${styles.chipActionMenuItemDanger}`}
                onClick={() => {
                  setOpenMenuId(null);
                  onDelete(contact);
                }}
              >
                <Trash2 size={13} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderBranchNode = (branch) => (
    <div className={styles.orgNode}>
      <p className={styles.orgNodeTitle}>
        <span
          className="detailBadge mb-0 contact-inherited cursor-pointer"
          onClick={() => goTo(branch.owner?.type, branch.owner?.id, branch.owner?.name)}
          title={`Go to ${branch.owner?.name || "owner"}`}
        >
          <span className={styles.badgeLabel}>{getLabelByType("entityType", branch.owner?.type)}</span>
          <span className={styles.badgeName}>{branch.owner?.name}</span>
        </span>
      </p>
      <div className={styles.orgNodeChips}>
        {branch.contacts.map((contact) => renderChip(contact, { editable: false }))}
      </div>
    </div>
  );

  const renderBranch = (branch, { above }) => (
    <div key={branch.owner?.id} className={styles.orgBranch}>
      <div className={above ? styles.orgConnectorAbove : styles.orgConnector} />
      {renderBranchNode(branch)}
    </div>
  );

  // A single branch within a level is a plain one-to-one relationship - just a straight
  // connecting line, no horizontal merge bar (that's only needed to fan out to siblings
  // at the same level, e.g. two Portfolios both contributing contacts under a Client).
  const renderAncestorLevel = ({ rank, branches }) => (
    <React.Fragment key={rank}>
      {branches.length === 1 ? (
        <>
          {renderBranchNode(branches[0])}
          <div className={styles.orgTrunk} />
        </>
      ) : (
        <>
          <div className={styles.orgBranchesAbove}>
            {branches.map((branch) => renderBranch(branch, { above: true }))}
          </div>
          <div className={styles.orgTrunk} />
        </>
      )}
    </React.Fragment>
  );

  const renderDescendantLevel = ({ rank, branches }) => (
    <React.Fragment key={rank}>
      {branches.length === 1 ? (
        <>
          <div className={styles.orgTrunk} />
          {renderBranchNode(branches[0])}
        </>
      ) : (
        <>
          <div className={styles.orgTrunk} />
          <div className={styles.orgBranches}>
            {branches.map((branch) => renderBranch(branch, { above: false }))}
          </div>
        </>
      )}
    </React.Fragment>
  );

  return (
    <div className={styles.orgChart}>
      {ancestorLevels.map(renderAncestorLevel)}

      <div className={`${styles.orgRoot} ${styles.orgRootCurrent}`}>
        <p className={styles.orgNodeTitle}>
          <span className={styles.currentName}>{parentName || "This entity"}</span>
        </p>
        <div className={styles.orgNodeChips}>
          {ownContacts.length === 0 ? (
            <span className="text-xsm-400 grey46Color">No direct contacts</span>
          ) : (
            ownContacts.map((contact) => renderChip(contact, { editable: true }))
          )}
        </div>
      </div>

      {descendantLevels.map(renderDescendantLevel)}
    </div>
  );
};

export default ContactOrgChart;
